/**
 * A barra de andamento da corretiva, no Dashboard.
 *
 * Papel: reproduz `.progress-wrap`, `.progress-bar`, `.progress-fill` e
 * `.progress-pct` (l. 354–364) e a cor por faixa de `progressColor` (l.
 * 812–817): 100 em verde, 75 em amarelo, 50 em laranja, e abaixo disso em
 * vermelho — faixa que nenhum status alcança.
 *
 * Como usar: o percentual vem de `PERCENTUAL_DO_ANDAMENTO`, em `rotulos.ts`. É o
 * percentual, e não o status, que escolhe a cor. O desenho no papel do Dashboard
 * (l. 470–473) está aqui, sob a marca `imprimindo-dashboard-de-manutencoes`: sem
 * largura mínima, trilho com a borda `--emb-borda-impressao`, percentual menor.
 */
import { cn } from '@/shared/lib/utils';

/** Os três percentuais que os status produzem. */
export type PercentualDoAndamento = 50 | 75 | 100;

/** A largura do preenchimento de cada percentual. */
const LARGURA: Readonly<Record<PercentualDoAndamento, string>> = {
  50: 'w-1/2',
  75: 'w-3/4',
  100: 'w-full',
};

/**
 * A cor do preenchimento pela faixa do percentual, como `progressColor`.
 *
 * @param percentual - O andamento.
 * @returns A classe de fundo.
 */
function corDaFaixa(percentual: number): string {
  if (percentual >= 100) return 'bg-sucesso';
  if (percentual >= 75) return 'bg-emb-amarelo';
  if (percentual >= 50) return 'bg-aviso';
  return 'bg-perigo';
}

/**
 * A barra com o percentual ao lado.
 *
 * @param percentual - 50, 75 ou 100.
 * @returns A barra.
 */
export function BarraDeAndamento({ percentual }: { percentual: PercentualDoAndamento }) {
  return (
    <div className="flex min-w-[160px] items-center gap-2 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:min-w-0 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:gap-1.5">
      <div className="h-2 flex-1 overflow-hidden rounded-[5px] border border-emb-borda bg-superficie-2 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:border-emb-borda-impressao">
        <div
          className={cn('h-full rounded-[5px]', LARGURA[percentual], corDaFaixa(percentual))}
        />
      </div>
      <span className="w-[34px] text-right font-outfit text-[11px] font-light text-texto-fraco print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:w-8 print:[body[data-impressao=imprimindo-dashboard-de-manutencoes]_&]:text-[10px]">
        {percentual}%
      </span>
    </div>
  );
}
